import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { type VehicleListing } from '@/services/vehicles/types';
import { vehicleTitle } from '@/utils/vehicleFormat';

/**
 * The visitor's car cart. Each line is a car with a quantity.
 *
 * Kept on the visitor's own device so it survives a refresh. It holds only what
 * the cart needs to render; prices and availability are re-read from the API
 * when the cart is shown, and the database prices the order again on submit.
 */
export type CarOrderItem = {
  id: string;
  slug: string;
  title: string;
  addedAt: string;
  quantity: number;
};

export const MAX_CAR_QUANTITY = 99;

const clampQuantity = (n: number) => Math.min(MAX_CAR_QUANTITY, Math.max(1, Math.floor(n) || 1));

/** Matches the limit enforced by `submit_car_order`. */
export const MAX_CAR_ORDER = 12;

type CarOrderState = {
  items: CarOrderItem[];
  add: (vehicle: Pick<VehicleListing, 'id' | 'slug' | 'make' | 'model' | 'year'>) => void;
  remove: (slug: string) => void;
  toggle: (vehicle: Pick<VehicleListing, 'id' | 'slug' | 'make' | 'model' | 'year'>) => void;
  clear: () => void;
  setQuantity: (slug: string, quantity: number) => void;
  /** Puts back items removed by remove/clear (undo), keeping their original order. */
  restore: (items: CarOrderItem[]) => void;
  has: (slug: string) => boolean;
};

export const useCarOrderStore = create<CarOrderState>()(
  persist(
    (set, get) => ({
      items: [],
      add: (vehicle) => {
        const { items } = get();
        if (items.length >= MAX_CAR_ORDER || items.some((i) => i.slug === vehicle.slug)) return;
        set({
          items: [
            ...items,
            {
              id: vehicle.id,
              slug: vehicle.slug,
              title: vehicleTitle(vehicle),
              addedAt: new Date().toISOString(),
              quantity: 1,
            },
          ],
        });
      },
      remove: (slug) => set({ items: get().items.filter((i) => i.slug !== slug) }),
      toggle: (vehicle) => (get().has(vehicle.slug) ? get().remove(vehicle.slug) : get().add(vehicle)),
      clear: () => set({ items: [] }),
      setQuantity: (slug, quantity) =>
        set({ items: get().items.map((i) => (i.slug === slug ? { ...i, quantity: clampQuantity(quantity) } : i)) }),
      restore: (restored) => {
        const known = new Set(get().items.map((i) => i.slug));
        const merged = [...get().items, ...restored.filter((i) => !known.has(i.slug))]
          .sort((a, b) => a.addedAt.localeCompare(b.addedAt))
          .slice(0, MAX_CAR_ORDER);
        set({ items: merged });
      },
      has: (slug) => get().items.some((i) => i.slug === slug),
    }),
    {
      name: 'kabex.car-order',
      version: 1,
      partialize: (state) => ({ items: state.items }),
    },
  ),
);
